import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { ReplaySubject } from 'rxjs/internal/ReplaySubject';
import { colorPalettes } from '../consts';

@Injectable({
  providedIn: 'root'
})
export class ThememodeService {

  private themeChanged$ = new ReplaySubject<void>();
  public themeChanged = this.themeChanged$.asObservable();

  private renderer!: Renderer2;
  private colorScheme!: string | null;
  // Define prefix for clearer and more readable class names in scss files
  private colorSchemePrefix = 'itopia-';
  private colorSchemaSufFix = '-v2';
  private themes = ['itopia-light', 'itopia-dark', 'itopia-light-v2', 'itopia-dark-v2'];
  version = 'v1';
  private FONT_LINK_ID = 'theme-font-link';

  constructor(rendererFactory: RendererFactory2) {
    // Create new renderer from renderFactory, to make it possible to use renderer2 in a service
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  detectPrefersColorScheme(): void {
    // Detect if prefers-color-scheme is supported
    if (window.matchMedia('(prefers-color-scheme)').media !== 'not all') {
      // Set colorScheme to Dark if prefers-color-scheme is dark. Otherwise set to light.
      this.colorScheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } else {
      // If the browser doesn't support prefers-color-scheme, set it as default to dark
      this.colorScheme = 'dark';
    }
  }

  setColorScheme(scheme: string): void {
    this.colorScheme = scheme;
    // Save prefers-color-scheme to localStorage
    localStorage.setItem('prefers-color', scheme);
  }

  getColorScheme(): string {
    this.colorScheme = 'light';
    // Check if any prefers-color-scheme is stored in localStorage
    if (localStorage.getItem('prefers-color')) {
      // Save prefers-color-scheme from localStorage
      this.colorScheme = localStorage.getItem('prefers-color');
      return this.colorScheme === 'dark' ? this.colorScheme : 'light';
    } else {
      // If no prefers-color-scheme is stored in localStorage, try to detect OS default prefers-color-scheme
      this.detectPrefersColorScheme();
    }
    return this.colorScheme;
  }

  load(version: string): void {
    const fontHref = version === 'v2'
      ? 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,300;0,400;0,500;0,700;1,100;1,300;1,400;1,500;1,700&display=swap'
      : 'https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;1,100;1,300;1,400;1,500;1,700&display=swap';
    this.setFontLink(fontHref);
    this.getColorScheme();
    this.version = version;
    if(version === 'v1'){
      this.renderer.addClass(document.body, this.colorSchemePrefix + this.colorScheme);
    }else{
      this.renderer.addClass(document.body, this.colorSchemePrefix + this.colorScheme + this.colorSchemaSufFix);
    }
    this.applyTheme(version as 'v1' | 'v2');
    this.setThemeChanged();
  }

  getThemeVersion(): string{
    return this.version;
  }

  update(scheme: string): void {
    this.setColorScheme(scheme);
    // Delete the old color-scheme class
    this.themes.forEach(t => document.body.classList.remove(t));
    // Add the new / current color-scheme class
    if(this.version === 'v1'){
      this.renderer.addClass(document.body, this.colorSchemePrefix + scheme);
    }else{
      this.renderer.addClass(document.body, this.colorSchemePrefix + scheme + this.colorSchemaSufFix);
    }
    this.setThemeChanged();
  }

  currentActive(): string | null {
    return this.colorScheme;
  }

  setThemeChanged(): void {
    this.themeChanged$.next();
  }
  applyStyleScheme(scheme: string): void {
    // Remove the old color-scheme class
    this.renderer.removeClass(document.body, this.colorSchemePrefix + (scheme === 'dark' ? 'light' : 'dark'));
    // Add the new / current color-scheme class
    this.renderer.addClass(document.body, this.colorSchemePrefix + scheme);
 }

 applyTheme(branding: 'v1' | 'v2'): void{
  const theme = colorPalettes[branding];

  if (!theme) {
    console.error(`Theme ${branding} not found.`);
    return;
  }

  Object.entries(theme).forEach(([key, value]) => {
    document.documentElement.style.setProperty(key, value);
  });

  console.log(`✅ Theme ${branding} applied.`);
}

private setFontLink(href: string) {
  let linkEl = document.getElementById(this.FONT_LINK_ID) as HTMLLinkElement;

  if (linkEl) {
    // Si ya existe, actualiza el href
    linkEl.href = href;
  } else {
    // Si no existe, crea y agrega
    linkEl = document.createElement('link');
    linkEl.id = this.FONT_LINK_ID;
    linkEl.rel = 'stylesheet';
    linkEl.href = href;
    document.head.appendChild(linkEl);
  }
}
}
